import blueGrey from '@material-ui/core/colors/blueGrey';
import cyan from '@material-ui/core/colors/cyan';
import green from '@material-ui/core/colors/green';
import orange from '@material-ui/core/colors/orange';
import red from '@material-ui/core/colors/red';
import teal from '@material-ui/core/colors/teal';
import { darkColors, lightColors } from 'UI/res/colors';

export const LIGHT_APP_BACKGROUND = '#FAFBFD';
const DARK_APP_BACKGROUND = '#0E1014';
const DARK_PAPER_BACKGROUND = '#1C1F26';

/**
 * Light palette. These values are the source of truth for light mode and the CSS-variable
 * fallbacks, so changing one changes the light look of the whole app.
 */
const lightPalette = {
  brandColors: {
    FPRed: '#D52028',
    FPWhite: '#ffffff'
  },
  action: {
    active: '#6A7381'
  },
  primary: {
    FP50: '#E7E9FA',
    FP100: '#D9E0FC',
    FP200: '#B3C0F8',
    FP300: '#8C9FF4',
    light: '#6F85EF',
    main: '#4056F4',
    dark: '#3343C8',
    FP700: '#2331A7',
    fill: '#4056F4',
    fillHover: '#3343C8',
    contrastText: '#E1E4EB'
  },
  secondary: {
    FP100: '#FAFBFF',
    FP200: '#F4F7FF',
    FP300: '#EFF3FF',
    light: lightColors.lightPurple,
    main: '#E5EAFE',
    dark: '#A7B0DB',
    FP700: '#737DB8',
    contrastText: '#030D28'
  },
  success: {
    FP100: '#D2FAD8',
    FP200: '#A5F6BA',
    FP300: '#72E69C',
    light: '#49CE86',
    main: '#27AE60',
    dark: '#009562',
    FP700: '#137D55',
    contrastText: '#E1E4EB'
  },
  info: {
    FP100: '#CBEFFD',
    FP200: '#99DBFB',
    FP300: '#63BEF5',
    light: '#3CA0EC',
    main: '#0275E0',
    dark: '#015AC0',
    FP700: '#0143A1',
    contrastText: LIGHT_APP_BACKGROUND
  },
  warning: {
    FP100: '#FFF3D3',
    FP200: '#FEE3A8',
    FP300: '#FDCF7D',
    light: '#FABB5F',
    main: '#F39C12',
    dark: '#D37C2D',
    FP700: '#B16022',
    contrastText: '#121417'
  },
  error: {
    FP100: '#FEE4D7',
    FP200: '#FCC3AF',
    FP300: '#F79B84',
    light: '#EF7465',
    main: '#E53935',
    dark: '#C3262F',
    FP700: '#A41B2F',
    contrastText: LIGHT_APP_BACKGROUND
  },
  grey: {
    50: LIGHT_APP_BACKGROUND,
    100: '#F8F9FB',
    200: '#F0F3F7',
    300: '#E4E8EE',
    400: '#CDD3DD',
    500: '#A8B1BD',
    600: '#6A7381',
    700: '#49505A',
    800: '#1F2329',
    900: '#030D28',
    A100: '#E6E9EE',
    A200: '#D3D7DD',
    A400: '#BAC0C9',
    A700: '#49505A',
    black: '#000'
  },
  status: {
    active: '#27AE60',
    sendout: '#4056F4',
    sendover: '#4735C9',
    placed: '#66CBCF',
    inactive: '#9B9EA7',
    deceased: '#525764'
  },
  red,
  green,
  teal,
  cyan,
  orange,
  blueGrey,
  customColors: {
    ...lightColors,
    appBackgroundContrast: '#ECEFF5',
    illustration: '#6A7381',
    blueStatus: '#50C9F5',
    candidateBackground: '#E2D9FB',
    candidateIcon: '#A38FF4',
    disabledIcon: '#abacae',
    inactiveInputText: '#00000099',
    navyBlue: '#25307C',
    softBack: '#F7FBFF',
    wolfImage: '#F6F8FA'
  }
};

/**
 * Dark palette ("Elevated graphite"). Every key keeps its light-mode role: low grey steps and
 * `FP100`-style tints are surfaces, high grey steps and `dark`/`FP700` shades are readable on those
 * surfaces. `primary.main` is the accent for text and icons; `primary.fill` / `fillHover` are the
 * deeper primaries behind white labels.
 */
const darkPalette = {
  type: 'dark',
  background: {
    default: DARK_APP_BACKGROUND,
    paper: DARK_PAPER_BACKGROUND
  },
  text: {
    primary: '#E6E9EF',
    secondary: '#A9B2BF',
    disabled: 'rgba(255, 255, 255, 0.38)',
    hint: 'rgba(255, 255, 255, 0.38)'
  },
  divider: '#3D434F',
  action: {
    active: '#A9B2BF',
    disabled: '#676E7B'
  },
  brandColors: {
    FPRed: '#D52028',
    FPWhite: '#ffffff'
  },
  primary: {
    FP50: '#292E3F',
    FP100: '#30364B',
    FP200: '#3D4564',
    FP300: '#596498',
    light: '#A6B4F3',
    main: '#8B9DF5',
    dark: '#7584CC',
    FP700: '#B4BFF2',
    fill: '#4A5FD8',
    fillHover: '#3E51C6',
    contrastText: '#0B1030'
  },
  secondary: {
    FP100: '#232732',
    FP200: '#262A39',
    FP300: '#292E3F',
    light: darkColors.lightPurple,
    main: '#383E52',
    dark: '#434B6E',
    FP700: '#C2CBF1',
    contrastText: '#E6E9EF'
  },
  success: {
    FP100: '#243936',
    FP200: '#294D42',
    FP300: '#347158',
    light: '#73CDA3',
    main: '#4CC38A',
    dark: '#4CC38A',
    FP700: '#8AD2B2',
    contrastText: '#0B1030'
  },
  info: {
    FP100: '#263747',
    FP200: '#2D485F',
    FP300: '#3B698C',
    light: '#7DC0F1',
    main: '#5AB2F2',
    dark: '#5AB2F2',
    FP700: '#92C8F1',
    contrastText: '#0B1030'
  },
  warning: {
    FP100: '#3E372B',
    FP200: '#58492E',
    FP300: '#876A35',
    light: '#EFC26F',
    main: '#F2B544',
    dark: '#F2B544',
    FP700: '#EDCA88',
    contrastText: '#0B1030'
  },
  error: {
    FP100: '#3E2D32',
    FP200: '#58373A',
    FP300: '#874B4A',
    light: '#EF938E',
    main: '#F2766E',
    dark: '#F2766E',
    FP700: '#EDA4A2',
    contrastText: '#0B1030'
  },
  grey: {
    50: DARK_APP_BACKGROUND,
    100: '#15171C',
    200: '#23272F',
    300: '#2A2E37',
    400: '#353A44',
    500: '#9199A7',
    600: '#A9B2BF',
    700: '#C8CED7',
    800: '#E6E9EF',
    900: '#F3F5F8',
    A100: '#22252D',
    A200: '#3D434F',
    A400: '#676E7B',
    A700: '#C8CED7',
    black: '#F3F5F8'
  },
  status: {
    active: '#4CC38A',
    sendout: '#8B9DF5',
    sendover: '#8872F0',
    placed: '#66CBCF',
    inactive: '#9B9EA7',
    deceased: '#8E98A7'
  },
  red,
  green,
  teal,
  cyan,
  orange,
  blueGrey,
  customColors: {
    ...darkColors,
    appBackgroundContrast: '#22252D',
    illustration: '#5A6170',
    blueStatus: '#50C9F5',
    candidateBackground: '#2E2650',
    candidateIcon: '#A38FF4',
    disabledIcon: '#676E7B',
    inactiveInputText: 'rgba(255, 255, 255, 0.6)',
    navyBlue: '#B3C0F8',
    softBack: '#15171C',
    wolfImage: '#23272F'
  }
};

export const palettesByMode = Object.freeze({ light: lightPalette, dark: darkPalette });
